/** Ordered PHP array storage. Clone received arrays with phpCopy before mutation,
 * exactly where PHP's copy-on-write would separate the caller's value. JS assignment
 * does not implement PHP assignment; helpers returning arrays return new storage.
 * bigint keys preserve native 64-bit keys that cannot fit a JS number exactly.
 */
export type PhpKey = string | number | bigint;
export class PhpFloat {
    constructor(public readonly value: number) {}
    valueOf(): number { return this.value; }
}
export type PhpKeyInput = PhpKey | boolean | null | PhpFloat;
const MIN = -9223372036854775808n, MAX = 9223372036854775807n;

export function normalizeKey(key: PhpKeyInput): PhpKey {
    if (key instanceof PhpFloat) key = key.value;
    if (key === null) return '';
    if (typeof key === 'boolean') return Number(key);
    if (typeof key === 'number') {
        key = Number.isFinite(key) ? BigInt.asIntN(64, BigInt(Math.trunc(key))) : 0n;
    } else if (typeof key === 'string') {
        if (!/^(?:0|-?[1-9]\d*)$/.test(key)) return key;
        const n = BigInt(key);
        if (n < MIN || n > MAX) return key;
        key = n;
    }
    if (typeof key === 'bigint') {
        if (key < MIN || key > MAX) throw new RangeError('PHP integer key outside int64');
        return key >= BigInt(Number.MIN_SAFE_INTEGER) && key <= BigInt(Number.MAX_SAFE_INTEGER) ? Number(key) : key;
    }
    return key;
}

export class PhpArray<K extends PhpKey = PhpKey, V = unknown> implements Iterable<[K, V]> {
    private storage = new Map<K, V>();
    private next = 0n;
    private hasInteger = false;
    private packed = true;
    private used = 0n;
    private capacity = 8n;
    /** Zend sampling slots, including internal holes left by unset(). */
    private randomSlots: (K | null)[] = [];
    get randomBucketKeys(): readonly (K | null)[] { return this.randomSlots; }
    constructor(entries: Iterable<readonly [PhpKeyInput, V]> = [], packed = true) {
        this.packed = packed;
        for (const [key, value] of entries) this.set(key, value);
    }
    get size(): number { return this.storage.size; }
    get isPacked(): boolean { return this.packed; }
    get(key: PhpKeyInput): V | undefined { return this.storage.get(normalizeKey(key) as K); }
    has(key: PhpKeyInput): boolean { return this.storage.has(normalizeKey(key) as K); }
    set(key: PhpKeyInput, value: V): this {
        const normalized = normalizeKey(key) as K;
        const inserted = !this.storage.has(normalized);
        const wasPacked = this.packed;
        if (this.packed && inserted) {
            // Zend packed insertion: filling an internal hole would reorder it;
            // sparse growth outside the table capacity converts to hash storage.
            const n = typeof normalized === 'string' ? -1n : BigInt(normalized);
            if (n < this.used) this.packed = false;
            else if (n >= this.capacity) {
                if ((n >> 1n) < this.capacity && (this.capacity >> 1n) < BigInt(this.size)) this.capacity *= 2n;
                else this.packed = false;
            }
            if (this.packed) this.used = n + 1n;
        }
        if (inserted) {
            if (wasPacked && !this.packed) this.randomSlots = [...this.storage.keys(), normalized];
            else if (this.packed) {
                while (this.randomSlots.length <= Number(normalized)) this.randomSlots.push(null);
                this.randomSlots[Number(normalized)] = normalized;
            } else this.randomSlots.push(normalized);
        }
        this.storage.set(normalized, value);
        if (typeof normalized !== 'string') {
            const n = BigInt(normalized);
            // PHP 8.3+: the first negative integer key establishes the next key too.
            if (this.packed && inserted || !this.hasInteger || n >= this.next) this.next = n === MAX ? MAX : n + 1n;
            this.hasInteger = true;
        }
        return this;
    }
    append(value: V): this {
        if (this.storage.has(normalizeKey(this.next) as K)) {
            const error = new Error('Cannot add element: next PHP key occupied'); error.name = 'Error'; throw error;
        }
        return this.set(this.next, value);
    }
    delete(key: PhpKeyInput): boolean {
        const deleted = this.storage.delete(normalizeKey(key) as K);
        if (deleted) {
            this.randomSlots[this.randomSlots.indexOf(normalizeKey(key) as K)] = null;
            while (this.randomSlots.length && this.randomSlots[this.randomSlots.length - 1] === null) this.randomSlots.pop();
        }
        if (deleted && this.packed) while (this.used > 0n && !this.has(this.used - 1n)) this.used--;
        return deleted;
    }
    clear(): void { this.storage.clear(); this.next = 0n; this.hasInteger = false; this.packed = true; this.used = 0n; this.capacity = 8n; this.randomSlots = []; }
    keys(): IterableIterator<K> { return this.storage.keys(); }
    values(): IterableIterator<V> { return this.storage.values(); }
    entries(): IterableIterator<[K, V]> { return this.storage.entries(); }
    [Symbol.iterator](): IterableIterator<[K, V]> { return this.entries(); }
    /** Preserves the append cursor (unset does not rewind PHP's next free index). */
    copy(map: (value: V) => V = value => value, order?: Iterable<K>, packed = this.packed): PhpArray<K, V> {
        const result = new PhpArray<K, V>();
        for (const key of order ?? this.storage.keys()) {
            if (!this.storage.has(key)) throw new RangeError('Cannot reorder a missing PHP array key');
            result.storage.set(key, map(this.storage.get(key)!));
        }
        result.next = this.next; result.hasInteger = this.hasInteger; result.packed = packed;
        result.used = this.used; result.capacity = this.capacity;
        result.randomSlots = order === undefined ? this.randomSlots.slice() : [...result.storage.keys()];
        return result;
    }
}
